import { useEffect, useRef, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";

interface Appointment {
  id: number;
  title: string;
  startTime: string;
  reminderMinutes: number | null;
  reminderSent: string | null;
}

export function useAppointmentReminders() {
  const { user, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const notifiedIds = useRef<Set<number>>(new Set());
  const permissionRequested = useRef(false);

  const { data: appointments = [] } = useQuery<Appointment[]>({
    queryKey: ["/api/appointments"],
    refetchInterval: 30000,
    enabled: !!user && !authLoading,
  });

  const markReminderSentMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest("PATCH", `/api/appointments/${id}/reminder-sent`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/appointments"] });
    },
  });

  const requestNotificationPermission = useCallback(async () => {
    if (permissionRequested.current) return;
    permissionRequested.current = true;

    if (!("Notification" in window)) {
      console.log("Browser does not support notifications");
      return;
    }

    if (Notification.permission === "default") {
      await Notification.requestPermission();
    }
  }, []);

  const showNotification = useCallback((appointment: Appointment): boolean => {
    if (Notification.permission !== "granted") return false;

    const startTime = new Date(appointment.startTime);
    const timeStr = startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const notification = new Notification(`Meeting Reminder: ${appointment.title}`, {
      body: `Starting at ${timeStr}`,
      icon: "/favicon.ico",
      tag: `appointment-${appointment.id}`,
      requireInteraction: true,
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
    };

    setTimeout(() => notification.close(), 30000);
    return true;
  }, []);

  useEffect(() => {
    if (user && !authLoading) {
      requestNotificationPermission();
    }
  }, [user, authLoading, requestNotificationPermission]);

  useEffect(() => {
    if (!user || authLoading) return;

    const checkReminders = () => {
      const now = new Date();

      appointments.forEach((apt) => {
        if (!apt.reminderMinutes || apt.reminderSent) return;
        if (notifiedIds.current.has(apt.id)) return;

        const startTime = new Date(apt.startTime);
        const reminderTime = new Date(startTime.getTime() - apt.reminderMinutes * 60 * 1000);

        if (now >= reminderTime && now < startTime) {
          notifiedIds.current.add(apt.id);
          const shown = showNotification(apt);
          if (shown) {
            markReminderSentMutation.mutate(apt.id);
          }
        }
      });
    };

    checkReminders();
    const interval = setInterval(checkReminders, 30000);

    return () => clearInterval(interval);
  }, [user, authLoading, appointments, showNotification, markReminderSentMutation]);

  return {
    requestNotificationPermission,
    notificationPermission: typeof Notification !== "undefined" ? Notification.permission : "denied",
  };
}
